/**
 * LAS CUENTAS QUE VE LA BANDEJA (commit 10 de multicuenta, MULTICUENTA-DISENO.md §5 y §5.1; 23-oct-2026).
 *
 * Tres decisiones puras, sin React ni Firestore, para que se prueben desde `functions/__tests__/` (un
 * test de `src/` no corre en el pre-commit):
 *
 *   · `opcionesDeCuenta`: qué cuentas ofrece el selector para el canal elegido. Unión de los docs de
 *     `channelAccounts` del tenant y de las cuentas que aparecen en los chats cargados (`cuentaDelChat`)
 *     sin documento → «desconectada» (se muestran, con etiqueta; decisión de Rodrigo, 23-oct).
 *   · `hayVariasCuentas`: el selector y la etiqueta de la tarjeta sólo existen cuando el canal tiene
 *     más de una cuenta en la org. Con una, nada cambia respecto a hoy.
 *   · `mensajeDeErrorDeEnvio`: el 409/503 del envío (`CHAT_SIN_CUENTA`, `NUMERO_NO_ES_EL_DEL_CHAT`,
 *     `CHAT_ILEGIBLE`, `sendMessageCore.js:751-763`) traducido; lo demás, como hoy.
 *
 * Es CommonJS como `cuentaDelChat.js`, por la misma razón: `functions/__tests__/` lo carga tal cual.
 */

const { cuentaDelChat, etiquetaDeCuenta } = require('./cuentaDelChat');

const CANALES = ['whatsapp', 'instagram', 'messenger', 'tiktok'];

/** `whatsapp_1126…` → `whatsapp`; `instagram_1784…` → `instagram`. */
function canalDeCuenta(accountId) {
  const canal = String(accountId || '').split('_')[0];
  return CANALES.includes(canal) ? canal : null;
}

/**
 * @param {{chats?: Array<object>, channelAccounts?: Array<object>, canal?: string}} p
 *   `channelAccounts`: docs del tenant con `id` (= accountId) y sus campos; `canal`: el del selector
 *   de arriba ('todos' | 'whatsapp' | …).
 * @returns {Array<{accountId: string, canal: string, principal: string, secundaria: string|null, desconectada: boolean}>}
 *   conectadas primero; dentro de cada grupo, por etiqueta.
 */
function opcionesDeCuenta({ chats = [], channelAccounts = [], canal = 'todos' } = {}) {
  const porId = new Map();
  for (const ca of channelAccounts) {
    const id = ca && (ca.accountId || ca.id);
    const c = canalDeCuenta(id);
    if (!id || !c) continue;
    if (canal !== 'todos' && c !== canal) continue;
    const etiqueta = etiquetaDeCuenta({ canal: c, ...ca });
    porId.set(id, { accountId: id, canal: c, principal: etiqueta ? etiqueta.principal : id, secundaria: etiqueta ? etiqueta.secundaria : null, desconectada: false });
  }
  for (const chat of chats) {
    const id = cuentaDelChat(chat);
    const c = canalDeCuenta(id);
    if (!id || !c || porId.has(id)) continue;
    if (canal !== 'todos' && c !== canal) continue;
    porId.set(id, { accountId: id, canal: c, principal: id.slice(c.length + 1), secundaria: null, desconectada: true });
  }
  return [...porId.values()].sort((a, b) => (a.desconectada - b.desconectada) || a.canal.localeCompare(b.canal) || a.principal.localeCompare(b.principal));
}

/**
 * ¿El canal tiene más de una cuenta? Con 'todos', si ALGÚN canal la tiene.
 * @param {string} canal
 * @param {ReturnType<typeof opcionesDeCuenta>} opciones  las de ESE canal, o las de todos
 */
function hayVariasCuentas(canal, opciones) {
  const porCanal = {};
  for (const o of opciones || []) porCanal[o.canal] = (porCanal[o.canal] || 0) + 1;
  if (canal === 'todos') return Object.values(porCanal).some((n) => n > 1);
  return (porCanal[canal] || 0) > 1;
}

/**
 * La etiqueta de cuenta de UNA tarjeta, o null si su canal no tiene varias cuentas (nada que distinguir).
 * @param {object} chat
 * @param {ReturnType<typeof opcionesDeCuenta>} opciones  las de 'todos' (todas las cuentas del tenant)
 */
function etiquetaDeChat(chat, opciones) {
  const id = cuentaDelChat(chat);
  const c = canalDeCuenta(id);
  if (!id || !c || !hayVariasCuentas(c, opciones)) return null;
  const o = (opciones || []).find((x) => x.accountId === id);
  if (!o) return { accountId: id, principal: id.slice(c.length + 1), desconectada: true };
  return { accountId: id, principal: o.principal, desconectada: o.desconectada };
}

const CODIGOS_TRADUCIDOS = ['CHAT_SIN_CUENTA', 'NUMERO_NO_ES_EL_DEL_CHAT', 'CHAT_ILEGIBLE'];

/**
 * El texto de la burbuja fallida. `t` es el de i18next con el namespace `chats`.
 * @param {{status: number, errData?: object, t: Function}} p
 */
function mensajeDeErrorDeEnvio({ status, errData, t }) {
  const codigo = errData && typeof errData.error === 'string' ? errData.error : null;
  if (codigo && CODIGOS_TRADUCIDOS.includes(codigo)) {
    const numero = errData.numeroIdDelChat ? String(errData.numeroIdDelChat).replace(/^wa_api_/, '') : '';
    return t(`detail.sendErrors.${codigo}`, { numero, defaultValue: codigo });
  }
  return codigo || `Error ${status}`;
}

module.exports = { canalDeCuenta, opcionesDeCuenta, hayVariasCuentas, etiquetaDeChat, mensajeDeErrorDeEnvio, CODIGOS_TRADUCIDOS };
